import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation } from "@tanstack/react-query";
import { Link } from "wouter";
import Navigation from "@/components/Navigation";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Handshake, 
  Building2, 
  Globe,
  Heart,
  Shield,
  Crown,
  Send,
  CheckCircle2,
  ArrowLeft,
  Users,
  BookOpen,
  Code,
  Mail,
  Phone
} from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

const formSchema = z.object({
  companyName: z.string().min(2, "Company name must be at least 2 characters"),
  contactName: z.string().min(2, "Contact name must be at least 2 characters"),
  email: z.string().email("Please enter a valid email address"),
  phone: z.string().optional(),
  website: z.string().url("Please enter a valid website URL").optional().or(z.literal("")),
  partnershipType: z.string().min(1, "Please select a partnership type"),
  companySize: z.string().min(1, "Please select your company size"),
  description: z.string().min(50, "Please provide at least 50 characters describing your partnership idea"),
  budget: z.string().optional(),
  timeline: z.string().min(1, "Please select your preferred timeline"),
  interests: z.array(z.string()).min(1, "Please select at least one area of interest"),
});

type FormData = z.infer<typeof formSchema>;

const partnershipTypes = [
  { value: "educational", label: "Educational Institution", icon: BookOpen },
  { value: "brand", label: "Children's Brand", icon: Heart },
  { value: "platform", label: "Content Platform", icon: Globe },
  { value: "technical", label: "Technical Integration", icon: Code },
  { value: "premium", label: "Premium Partnership", icon: Crown },
  { value: "other", label: "Other", icon: Building2 },
];

const companySizes = [
  { value: "startup", label: "Startup (1-10 employees)" },
  { value: "small", label: "Small Business (11-50 employees)" },
  { value: "medium", label: "Medium Business (51-200 employees)" },
  { value: "large", label: "Large Company (201+ employees)" },
  { value: "enterprise", label: "Enterprise (1000+ employees)" },
];

const timelines = [
  { value: "immediate", label: "Immediate (1-2 weeks)" },
  { value: "short", label: "Short-term (1-3 months)" },
  { value: "medium", label: "Medium-term (3-6 months)" },
  { value: "long", label: "Long-term (6+ months)" },
  { value: "flexible", label: "Flexible" },
];

const interestAreas = [
  { id: "integration", label: "API Integration" },
  { id: "cobranding", label: "Co-branded Content" },
  { id: "distribution", label: "Content Distribution" },
  { id: "licensing", label: "Content Licensing" },
  { id: "marketing", label: "Cross-marketing" },
  { id: "education", label: "Educational Programs" },
];

const partnershipBenefits = [
  {
    icon: Globe,
    title: "Global Reach",
    description: "Access to our worldwide user base of families"
  },
  {
    icon: Code,
    title: "API Access",
    description: "Integration with our powerful story generation platform"
  },
  {
    icon: Users,
    title: "Co-marketing",
    description: "Joint marketing campaigns and promotional opportunities"
  },
  {
    icon: Crown,
    title: "Premium Support",
    description: "Dedicated partnership manager and technical support"
  }
];

export default function PartnershipsContactPage() {
  const { toast } = useToast();
  const [isSubmitted, setIsSubmitted] = useState(false);

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      companyName: "",
      contactName: "",
      email: "",
      phone: "",
      website: "",
      partnershipType: "",
      companySize: "",
      description: "",
      budget: "",
      timeline: "",
      interests: [],
    },
  });

  const submitMutation = useMutation({
    mutationFn: async (data: FormData) => {
      return apiRequest('POST', '/api/partnerships/contact', data);
    },
    onSuccess: () => {
      setIsSubmitted(true);
      toast({
        title: "Partnership Inquiry Sent!",
        description: "We've received your partnership inquiry and will get back to you within 2 business days.",
      });
    },
    onError: (error) => {
      toast({
        title: "Failed to Send",
        description: "There was an error sending your partnership inquiry. Please try again.",
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: FormData) => {
    submitMutation.mutate(data);
  };

  if (isSubmitted) {
    return (
      <>
        <Navigation />
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
          <div className="container mx-auto px-4 py-16 max-w-2xl">
            <Card className="text-center">
              <CardContent className="pt-8 pb-8">
                <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h1 className="text-3xl font-bold mb-4">Partnership Inquiry Sent!</h1>
                <p className="text-muted-foreground mb-6">
                  Thank you for your interest in partnering with SnuggleCrafters. We've received your inquiry 
                  and our partnerships team will review it and get back to you within 2 business days.
                </p>
                <div className="flex gap-4 justify-center">
                  <Link href="/programs">
                    <Button variant="outline" data-testid="button-back-programs">
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Back to Programs
                    </Button>
                  </Link>
                  <Link href="/contact">
                    <Button data-testid="button-general-contact">
                      General Contact
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navigation />
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
        <div className="container mx-auto px-4 py-8 max-w-6xl">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Handshake className="w-4 h-4" />
              Partnership Contact
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Let's Partner Together
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Interested in partnering with SnuggleCrafters? We're always looking for innovative collaborations 
              that can bring magical bedtime stories to more families worldwide.
            </p>
          </div>

          {/* Partnership Benefits */}
          <div className="grid md:grid-cols-4 gap-4 mb-12">
            {partnershipBenefits.map((benefit, index) => (
              <Card key={index} className="text-center p-4">
                <benefit.icon className="w-8 h-8 text-blue-500 mx-auto mb-2" />
                <h3 className="font-semibold mb-1">{benefit.title}</h3>
                <p className="text-sm text-muted-foreground">{benefit.description}</p>
              </Card>
            ))}
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Partnership Form */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Send className="w-5 h-5" />
                  Partnership Inquiry
                </CardTitle>
                <CardDescription>
                  Tell us about your company and partnership vision
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                    {/* Company Information */}
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold">Company Information</h3>
                      <div className="grid md:grid-cols-2 gap-4">
                        <FormField
                          control={form.control}
                          name="companyName"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Company Name *</FormLabel>
                              <FormControl>
                                <Input placeholder="Your company name" {...field} data-testid="input-company-name" />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="contactName"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Contact Name *</FormLabel>
                              <FormControl>
                                <Input placeholder="Your full name" {...field} data-testid="input-contact-name" />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                      <div className="grid md:grid-cols-2 gap-4">
                        <FormField
                          control={form.control}
                          name="email"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Email Address *</FormLabel>
                              <FormControl>
                                <Input placeholder="your@company.com" {...field} data-testid="input-email" />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="phone"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Phone Number</FormLabel>
                              <FormControl>
                                <Input placeholder="+1 (555) 123-4567" {...field} data-testid="input-phone" />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                      <FormField
                        control={form.control}
                        name="website"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Company Website</FormLabel>
                            <FormControl>
                              <Input placeholder="https://yourcompany.com" {...field} data-testid="input-website" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <div className="grid md:grid-cols-2 gap-4">
                        <FormField
                          control={form.control}
                          name="partnershipType"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Partnership Type *</FormLabel>
                              <Select onValueChange={field.onChange} value={field.value}>
                                <FormControl>
                                  <SelectTrigger data-testid="select-partnership-type">
                                    <SelectValue placeholder="Select partnership type" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {partnershipTypes.map((type) => (
                                    <SelectItem key={type.value} value={type.value}>
                                      {type.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="companySize"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Company Size *</FormLabel>
                              <Select onValueChange={field.onChange} value={field.value}>
                                <FormControl>
                                  <SelectTrigger data-testid="select-company-size">
                                    <SelectValue placeholder="Select company size" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {companySizes.map((size) => (
                                    <SelectItem key={size.value} value={size.value}>
                                      {size.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>

                    {/* Partnership Details */}
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold">Partnership Details</h3>
                      
                      <FormField
                        control={form.control}
                        name="description"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Partnership Description *</FormLabel>
                            <FormControl>
                              <Textarea
                                placeholder="Describe your partnership vision, goals, and how you envision working together..."
                                className="min-h-[100px]"
                                {...field}
                                data-testid="textarea-description"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <div className="grid md:grid-cols-2 gap-4">
                        <FormField
                          control={form.control}
                          name="timeline"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Preferred Timeline *</FormLabel>
                              <Select onValueChange={field.onChange} value={field.value}>
                                <FormControl>
                                  <SelectTrigger data-testid="select-timeline">
                                    <SelectValue placeholder="Select timeline" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {timelines.map((timeline) => (
                                    <SelectItem key={timeline.value} value={timeline.value}>
                                      {timeline.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="budget"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Budget Range (Optional)</FormLabel>
                              <FormControl>
                                <Input placeholder="$10,000 - $50,000" {...field} data-testid="input-budget" />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <FormField
                        control={form.control}
                        name="interests"
                        render={() => (
                          <FormItem>
                            <FormLabel>Areas of Interest *</FormLabel>
                            <FormDescription>Select all that apply</FormDescription>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                              {interestAreas.map((interest) => (
                                <FormField
                                  key={interest.id}
                                  control={form.control}
                                  name="interests"
                                  render={({ field }) => (
                                    <FormItem className="flex items-center space-x-2">
                                      <FormControl>
                                        <Checkbox
                                          checked={field.value?.includes(interest.id)}
                                          onCheckedChange={(checked) => {
                                            return checked
                                              ? field.onChange([...field.value, interest.id])
                                              : field.onChange(
                                                  field.value?.filter((value) => value !== interest.id)
                                                );
                                          }}
                                          data-testid={`checkbox-${interest.id}`}
                                        />
                                      </FormControl>
                                      <FormLabel className="text-sm font-normal">
                                        {interest.label}
                                      </FormLabel>
                                    </FormItem>
                                  )}
                                />
                              ))}
                            </div>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <div className="flex gap-4">
                      <Link href="/programs">
                        <Button variant="outline" type="button" data-testid="button-back">
                          <ArrowLeft className="w-4 h-4 mr-2" />
                          Back
                        </Button>
                      </Link>
                      <Button 
                        type="submit" 
                        disabled={submitMutation.isPending}
                        className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                        data-testid="button-submit-inquiry"
                      >
                        {submitMutation.isPending ? "Sending..." : "Send Partnership Inquiry"}
                        <Send className="w-4 h-4 ml-2" />
                      </Button>
                    </div>
                  </form>
                </Form>
              </CardContent>
            </Card>

            {/* Partnership Information */}
            <div className="space-y-6">
              {/* Partnership Types */}
              <Card>
                <CardHeader>
                  <CardTitle>Partnership Opportunities</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {partnershipTypes.slice(0, -1).map((type, index) => (
                      <div key={index} className="flex items-start gap-3 p-3 bg-muted rounded-lg">
                        <type.icon className="w-5 h-5 text-blue-600 mt-0.5" />
                        <div>
                          <h4 className="font-medium">{type.label}</h4>
                          <p className="text-sm text-muted-foreground">
                            {type.value === 'educational' && 'Schools, libraries, and learning centers'}
                            {type.value === 'brand' && 'Toy companies, publishers, and family brands'}
                            {type.value === 'platform' && 'Apps, websites, and content platforms'}
                            {type.value === 'technical' && 'API integrations and technical solutions'}
                            {type.value === 'premium' && 'Exclusive partnerships for established brands'}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Contact Information */}
              <Card>
                <CardHeader>
                  <CardTitle>Direct Contact</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <Mail className="w-5 h-5 text-blue-600" />
                      <div>
                        <h4 className="font-medium">Partnerships Team</h4>
                        <p className="text-sm text-muted-foreground">partnerships@snugglecrafters.com</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Phone className="w-5 h-5 text-blue-600" />
                      <div>
                        <h4 className="font-medium">Partnership Hotline</h4>
                        <p className="text-sm text-muted-foreground">+1-555-PARTNER (Mon-Fri 9AM-6PM EST)</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Response Time */}
              <div className="bg-blue-50 dark:bg-blue-950 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
                <h4 className="font-medium mb-2 flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-blue-600" />
                  What to Expect
                </h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Initial response within 2 business days</li>
                  <li>• Partnership assessment call within 1 week</li>
                  <li>• Detailed partnership proposal if aligned</li>
                  <li>• Dedicated partnership manager assigned</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}